import api from '@/utils/api';

export interface CustomerProfileFilters {
  search?: string;
  gender?: string;
  lead_bucket?: string;
  converted?: boolean | null;
  filled_only?: boolean;
  date_from?: string;
  date_to?: string;
  age_min?: number;
  age_max?: number;
  weight_min?: number;
  weight_max?: number;
  agent?: string;
}

export interface AgentCoverageRow {
  agent_id: number;
  agent_name: string;
  total_leads: number;
  complete_count: number;
  incomplete_count: number;
  missing_count: number;
  complete_pct: number;
}

export interface CustomerProfileSummary {
  total_profiles: number;
  filled_profiles: number;
  converted_profiles: number;
  conversion_pct: number;
  orders_after_profile: number;
  revenue_after_profile: number;
}

export interface CustomerProfileRow {
  profile_uuid: string;
  phone: string;
  gender: string | null;
  age: number | null;
  height_cm: string | null;
  weight_kg: string | null;
  primary_goal: string | null;
  health_issues: string | null;
  family_medical_history: string | null;
  created_at: string | null;
  updated_at: string | null;
  lead_uuid: string | null;
  lead_name: string | null;
  owner_name: string | null;
  lead_bucket: string | null;
  converted: boolean;
  order_count: number;
  order_value: number;
  last_order_at: string | null;
}

/** Build a query string from filters, skipping empty values. */
function buildQuery(params: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      qs.append(k, String(v));
    }
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export async function getCustomerProfilesSummary(filters: CustomerProfileFilters) {
  const q = buildQuery({
    search: filters.search,
    gender: filters.gender,
    lead_bucket: filters.lead_bucket,
    date_from: filters.date_from,
    date_to: filters.date_to,
    age_min: filters.age_min,
    age_max: filters.age_max,
    weight_min: filters.weight_min,
    weight_max: filters.weight_max,
    agent: filters.agent,
  });
  return api.get<CustomerProfileSummary>(`/customer-profiles/summary${q}`);
}

export async function getCustomerProfiles(
  filters: CustomerProfileFilters,
  page: number,
  pageSize: number,
) {
  const q = buildQuery({
    page,
    page_size: pageSize,
    search: filters.search,
    gender: filters.gender,
    lead_bucket: filters.lead_bucket,
    converted: filters.converted,
    filled_only: filters.filled_only,
    date_from: filters.date_from,
    date_to: filters.date_to,
    age_min: filters.age_min,
    age_max: filters.age_max,
    weight_min: filters.weight_min,
    weight_max: filters.weight_max,
    agent: filters.agent,
  });
  return api.get<CustomerProfileRow[]>(`/customer-profiles${q}`);
}

export async function getAgentProfileCoverage(dateFrom?: string, dateTo?: string) {
  const q = buildQuery({ date_from: dateFrom, date_to: dateTo });
  return api.get<AgentCoverageRow[]>(`/customer-profiles/agent-coverage${q}`);
}

/**
 * Fetch a CSV route as a blob and trigger a browser download. The shared api
 * client always parses JSON, so this fetches directly (with the same auth
 * cookie/bearer) instead.
 */
async function downloadCsv(path: string, filename: string): Promise<void> {
  const baseUrl = import.meta.env.VITE_API_URL || 'https://api.growtheye.com/api';

  // Reuse the same token resolution the api client uses (cookie first).
  let token: string | null = null;
  const tokenCookie = document.cookie
    .split(';')
    .find((c) => c.trim().startsWith('crm_auth_token='));
  if (tokenCookie) token = tokenCookie.split('=')[1];
  else if (import.meta.env.VITE_BEARER_TOKEN) token = import.meta.env.VITE_BEARER_TOKEN;

  const res = await fetch(`${baseUrl}${path}`, {
    method: 'GET',
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) {
    throw new Error(`Export failed: ${res.status} ${res.statusText}`);
  }

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function exportCustomerProfiles(filters: CustomerProfileFilters): Promise<void> {
  const q = buildQuery({
    search: filters.search,
    gender: filters.gender,
    lead_bucket: filters.lead_bucket,
    converted: filters.converted,
    filled_only: filters.filled_only,
    date_from: filters.date_from,
    date_to: filters.date_to,
    age_min: filters.age_min,
    age_max: filters.age_max,
    weight_min: filters.weight_min,
    weight_max: filters.weight_max,
    agent: filters.agent,
  });
  await downloadCsv(
    `/customer-profiles/export${q}`,
    `customer-profiles-${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

export async function exportAgentProfileCoverage(dateFrom?: string, dateTo?: string): Promise<void> {
  const q = buildQuery({ date_from: dateFrom, date_to: dateTo });
  await downloadCsv(
    `/customer-profiles/agent-coverage/export${q}`,
    `agent-profile-coverage-${new Date().toISOString().slice(0, 10)}.csv`,
  );
}
